import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icons } from '../components/Icons';
import { getAnnouncements, getArchivedAnnouncements, dismissAnnouncement, restoreAnnouncement, getAnnouncementImageUrl } from '../services/api';
import { AnnouncementsSkeleton } from '../components/SkeletonLoader';
import { useWebSocketEvent } from '../hooks/useWebSocket';

// Renders announcement content preserving line breaks, and turns any HTTP(S)
// URLs into clickable links that open in a new tab.
const renderContent = (text) => {
  const urlRegex = /(https?:\/\/[^\s<>"')\]]+)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(match[0]);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#086C53] font-semibold underline hover:text-[#065440]"
      >
        {part}
      </a>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
};

const getTagColor = (tag) => {
  const map = {
    General: 'bg-[#bbf7d0] text-green-800',
    Info: 'bg-blue-100 text-blue-700',
    Urgent: 'bg-red-100 text-red-700',
  };
  return map[tag] || 'bg-gray-100 text-gray-600';
};

const formatPostedDate = (dateStr, monthLength = 'short') =>
  dateStr
    ? new Date(dateStr).toLocaleDateString('en-US', { month: monthLength, day: 'numeric', year: 'numeric' })
    : '';

const OrgAnnouncements = () => {
  const [activeTab, setActiveTab] = useState('Active'); // 'Active' or 'Archived'
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  // The announcement currently open in the detail modal (null = closed)
  const [viewingAnnouncement, setViewingAnnouncement] = useState(null);

  // ID of the announcement pending archive (null = no modal shown)
  const [pendingArchiveId, setPendingArchiveId] = useState(null);

  // Fetches either the active list or the archived list depending on the active tab
  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const res = activeTab === 'Active'
        ? await getAnnouncements()
        : await getArchivedAnnouncements();
      setAnnouncements(res.data);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchAnnouncements();
  }, [activeTab]);

  // Real-time synchronization
  useWebSocketEvent("ANNOUNCEMENT_UPDATED", () => {
    fetchAnnouncements();
  });

  // Close the detail/confirm modals with the Escape key
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setViewingAnnouncement(null);
        setPendingArchiveId(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleArchiveClick = (id) => setPendingArchiveId(id);
  const handleCancelArchive = () => setPendingArchiveId(null);

  // Archives the announcement for this organization only — other orgs
  // still see it normally. This does NOT delete the announcement itself.
  const handleConfirmArchive = async () => {
    try {
      await dismissAnnouncement(pendingArchiveId);
      setAnnouncements((prev) => prev.filter((a) => a.announcement_id !== pendingArchiveId));
    } catch (error) {
      console.error('Failed to archive announcement:', error);
      alert('Failed to archive this announcement. Please try again.');
    } finally {
      setPendingArchiveId(null);
    }
  };

  // Restores an archived announcement back to the Active list
  const handleRestore = async (id) => {
    try {
      await restoreAnnouncement(id);
      setAnnouncements((prev) => prev.filter((a) => a.announcement_id !== id));
    } catch (error) {
      console.error('Failed to restore announcement:', error);
      alert('Failed to restore this announcement. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-56 mb-4" />
        <AnnouncementsSkeleton count={4} />
      </div>
    );
  }

  return (
    <div className="space-y-6 relative pb-16">
      <div>
        <h2 className="text-[28px] font-bold text-gray-900 tracking-tight">Announcements</h2>
        <p className="text-gray-500 text-sm mt-1">Stay updated with the latest news and information from OSAS.</p>
      </div>

      {/* Active / Archived Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-xl w-fit">
        {['Active', 'Archived'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-5 py-2 rounded-lg text-sm font-bold transition-colors ${
              activeTab === tab
                ? 'bg-white text-[#086C53] shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {announcements.map((announcement) => {
          const tag = announcement.category || 'General';
          return (
            <div
              key={announcement.announcement_id}
              onClick={() => setViewingAnnouncement(announcement)}
              className="bg-white rounded-[20px] border border-gray-200 shadow-sm hover:shadow-md hover:border-[#086C53]/40 transition-all cursor-pointer group"
            >
              <div className="p-7">
                <div className="flex flex-col space-y-3">
                  <div className="flex justify-between items-start">
                    <span className={`${getTagColor(tag)} text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider h-fit`}>
                      {tag}
                    </span>

                    {/* Right side: date + action button */}
                    <div className="flex items-center space-x-2 flex-shrink-0 pl-2">
                      <div className="text-[11px] text-gray-400 font-medium whitespace-nowrap bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100">
                        Posted {formatPostedDate(announcement.created_at)}
                      </div>
                      {activeTab === 'Active' ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArchiveClick(announcement.announcement_id);
                          }}
                          title="Archive announcement"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border border-red-100 bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-600 hover:border-red-200 transition-colors flex-shrink-0"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRestore(announcement.announcement_id);
                          }}
                          title="Restore announcement"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border border-green-100 bg-green-50 text-green-500 hover:bg-green-100 hover:text-green-700 hover:border-green-200 transition-colors flex-shrink-0"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v0M3 10l6-6M3 10l6 6" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pr-2">
                    <h3 className="text-lg font-bold text-gray-900 break-words group-hover:text-[#086C53] transition-colors flex-1">
                      {announcement.title}
                    </h3>
                    {announcement.image_path && (
                      <span className="flex items-center gap-1 text-[10px] font-bold text-[#086C53] bg-[#e0f5ee] px-2 py-0.5 rounded-full uppercase tracking-wider flex-shrink-0">
                        <Icons.Upload className="w-3 h-3" />
                        Image
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {announcements.length === 0 && (
          <div className="bg-white rounded-[20px] border border-gray-200 shadow-sm p-12 text-center flex flex-col items-center justify-center">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 text-gray-300">
              <Icons.Announcement />
            </div>
            <h3 className="text-lg font-bold text-gray-700 mb-1">
              {activeTab === 'Active' ? 'No Announcements Yet' : 'No Archived Announcements'}
            </h3>
            <p className="text-sm text-gray-500">
              {activeTab === 'Active'
                ? 'Check back later for updates from the administration.'
                : 'Announcements you archive will show up here.'}
            </p>
          </div>
        )}
      </div>

      {/* ── Announcement Detail Modal ── */}
      {viewingAnnouncement &&
        createPortal(
          <div className="fixed top-0 left-0 w-screen h-screen z-[9999] flex items-center justify-center p-4 sm:p-6">
            <div
              className="absolute top-0 left-0 w-full h-full bg-black/50 backdrop-blur-[3px]"
              onClick={() => setViewingAnnouncement(null)}
            />

            <div className="relative z-10 bg-white rounded-[24px] shadow-2xl border border-gray-100 w-fit min-w-[min(400px,90vw)] max-w-[92vw] max-h-[90vh] overflow-hidden flex flex-col">
              {/* Scroll area — clipped by the rounded outer container so the
                  scrollbar doesn't override the corners */}
              <div className="announcement-ui-scroll overflow-y-auto flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-7 py-4 sticky top-0 bg-white z-10">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`${getTagColor(viewingAnnouncement.category || 'General')} text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider flex-shrink-0`}>
                      {viewingAnnouncement.category || 'General'}
                    </span>
                    <span className="text-[11px] text-gray-400 font-medium whitespace-nowrap bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-100">
                      Posted {formatPostedDate(viewingAnnouncement.created_at, 'long')}
                    </span>
                  </div>
                  <button
                    onClick={() => setViewingAnnouncement(null)}
                    className="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                    title="Close"
                  >
                    <Icons.Close />
                  </button>
                </div>

                {/* Body */}
                <div className="px-7 py-6">
                  <h2 className="text-xl font-bold text-gray-900 break-words mb-5">
                    {viewingAnnouncement.title}
                  </h2>

                  {viewingAnnouncement.image_path && (
                    <div className="flex justify-center mb-6">
                      <img
                        src={getAnnouncementImageUrl(viewingAnnouncement)}
                        alt={viewingAnnouncement.title}
                        className="max-h-[70vh] h-auto w-auto max-w-full object-contain rounded-xl border border-gray-100 bg-gray-50"
                      />
                    </div>
                  )}

                  <div className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap break-words">
                    {renderContent(viewingAnnouncement.content)}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* ── Confirmation Modal ── */}
      {pendingArchiveId !== null &&
        createPortal(
          <div className="fixed top-0 left-0 w-screen h-screen z-[9999] flex items-center justify-center">
            <div
              className="absolute top-0 left-0 w-full h-full bg-black/40 backdrop-blur-[2px]"
              onClick={handleCancelArchive}
            />

            <div className="relative z-10 bg-white rounded-[20px] shadow-2xl border border-gray-100 p-8 w-full max-w-sm mx-4 animate-fade-in">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-red-600">
                <Icons.Trash />
              </div>

              <h3 className="text-[18px] font-bold text-gray-900 text-center mb-1">Archive this announcement?</h3>
              <p className="text-sm text-gray-500 text-center mb-8">
                You can restore it later from the Archived tab.
              </p>

              <div className="flex space-x-3">
                <button
                  onClick={handleCancelArchive}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmArchive}
                  className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold transition-colors"
                >
                  Yes, Archive
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default OrgAnnouncements;